import { useQuery, useMutation, useQueryClient } from "react-query";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import {
  getCompanyInformation,
  saveCompanyInformation,
  deleteCompanyInformation,
  destroyProIntent,
} from "../services/companyInfo";
import { ICompanyInfoForm } from "../types";

export const useCompanyInformation = () => {
  const { currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clientProId = (currentUser as any)?.clientProId;

  const {
    data: companyInformation,
    isLoading: isLoadingCompanyInformation,
    isFetching: isFetchingCompanyInformation,
    refetch: refetchCompanyInformation,
  } = useQuery(
    ["companyInformation", clientProId],
    () => {
      if (!clientProId) {
        return Promise.resolve(null);
      }
      return getCompanyInformation(clientProId);
    },
    {
      enabled: !!clientProId,
    }
  );

  const saveMutation = useMutation(
    (payload: ICompanyInfoForm) => {
      return saveCompanyInformation({ id: clientProId, ...payload });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("currentUser");
        refetchCompanyInformation();
      },
    }
  );

  const deleteMutation = useMutation(
    () => {
      if (!clientProId) {
        return Promise.reject(new Error("Missing client pro Id."));
      }
      return deleteCompanyInformation(clientProId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("currentUser");
        refetchCompanyInformation();
      },
    }
  );

  const destroyProIntentMutation = useMutation(
    () => {
      return destroyProIntent();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("currentUser"); // Assuming this clears the pro intent flag
      },
    }
  );

  const normalizedCompanyInformation =
    companyInformation?.data ?? null;

  return {
    companyInformation: normalizedCompanyInformation,
    isLoadingCompanyInformation,
    isFetchingCompanyInformation,
    refetchCompanyInformation,
    saveCompanyInformation: saveMutation.mutateAsync,
    isSavingCompanyInformation: saveMutation.isLoading,
    deleteCompanyInformation: deleteMutation.mutateAsync,
    isDeletingCompanyInformation: deleteMutation.isLoading,
    destroyProIntent: destroyProIntentMutation.mutateAsync,
    isDestroyingProIntent: destroyProIntentMutation.isLoading,
  };
};
